import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Card, Row, Col } from 'react-bootstrap';
import { CalendarEvent, Person, Building, ChatQuote, JournalText, ClipboardCheck, ClockHistory } from 'react-bootstrap-icons';

function HouseholdAidHistory({ client, refreshKey }) {
  const navigate = useNavigate();
  const clientID = client?.clientID; 
  const [aidHistory, setAidHistory] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);

  const handleRowClick = (visit) => {
    setSelectedVisit(visit);
  };
  const handleCloseModal = () => {
    setSelectedVisit(null);
  };

  useEffect(() => {
    if (!clientID) {
      console.log("Client ID not found");
      return;
    }

    const fetchAidHistory = async () => {
      try {
        const sessionID = Cookies.get("sessionID");

        if (!sessionID) {
          alert("Session expired. Please log in again.");
          navigate("/SignIn");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_BACKURL}/api/foodPantry/visit/household?clientID=${clientID}`,
          {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${sessionID}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        let rawVisits = response.data.visits;
        if (!Array.isArray(rawVisits) && typeof rawVisits === 'object' && rawVisits !== null) {
            rawVisits = Object.values(rawVisits);
        } else if (!Array.isArray(rawVisits)) {
            rawVisits = [];
        }
        
        // Sort data by date descending
        const sortedData = rawVisits.sort((a, b) => new Date(b.date) - new Date(a.date));
        setAidHistory(sortedData);

      } catch (error) {
        console.error("Error fetching aid history", error);
        setAidHistory([]);
      }
    };

    fetchAidHistory();
  }, [navigate, clientID, refreshKey]);

  return (
    <React.Fragment>
      <div className="card border border-light m-0 h-100">
        <div className="card-header border-bottom border-light">
          <h3 className="text-center mb-1">Household Aid History</h3>
        </div>
        <div className="card-body">
          <div className="table-responsive"> 
            <table className="table table-sm table-hover table-bordered">
              <thead className="table-light">
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">First Name</th>
                  <th scope="col">Last Name</th>
                  <th scope="col">Organization</th>
                </tr>
              </thead>
              <tbody className="table-group-divider text-black">
                {aidHistory && aidHistory.length > 0 ? (
                  aidHistory.map((visit) => (
                    <tr 
                      key={visit.visitID} 
                      onClick={() => handleRowClick(visit)} 
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{new Date(visit.date).toLocaleDateString()}</td>
                      <td>{visit.firstName}</td>
                      <td>{visit.lastName}</td>
                      <td>{visit.orgName}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No aid history found for this household.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal for Detailed View */}
      <Modal show={!!selectedVisit} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="text-dark fs-4">Visit Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 text-dark fs-5">
          {selectedVisit && (
            <>
              <Card className="bg-light border-0 text-black mb-3">
                <Card.Body>
                  <h6 className="card-title text-black fs-5 mb-3">Client & Service</h6>
                  <div className="d-flex align-items-center mb-2">
                    <Person className="me-2 text-primary" size={20} />
                    <div className="fs-6">
                      <strong>Client:</strong> {selectedVisit.firstName} {selectedVisit.lastName}
                    </div>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <Building className="me-2 text-primary" size={20} />
                    <div className="fs-6">
                      <strong>Organization:</strong> {selectedVisit.orgName}
                    </div>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <ChatQuote className="me-2 text-primary" size={20} />
                    <div className="fs-6">
                      <strong>Service Type:</strong> {selectedVisit.serviceType}
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Visit Info Card */}
              <Card className="bg-light border-0 text-black mb-3">
                <Card.Body>
                  <h6 className="card-title text-black fs-5 mb-3">Visit Summary</h6>
                  <div className="d-flex align-items-center mb-2">
                    <CalendarEvent className="me-2 text-secondary" size={20} />
                    <div className="fs-6">
                      <strong>Date:</strong> {new Date(selectedVisit.date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <JournalText className="me-2 text-danger" size={20} />
                    <div className="fs-6">
                      <strong>Reason:</strong> {selectedVisit.reason || 'N/A'}
                    </div>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <ClipboardCheck className="me-2 text-success" size={20} />
                    <div className="fs-6">
                      <strong>Outcome:</strong> {selectedVisit.outcome || 'N/A'}
                    </div>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <ClockHistory className="me-2 text-warning" size={20} />
                    <div className="fs-6">
                      <strong>Follow-Up Date:</strong> {selectedVisit.followUp ? new Date(selectedVisit.followUp).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Detailed Notes Card */}
              <Card className="bg-light border-0 text-black">
                <Card.Body>
                  <h6 className="card-title text-black fs-5 mb-3">Notes</h6>
                  <div className="d-flex align-items-start mb-2">
                    <JournalText className="me-2 text-info" size={20} />
                    <p className="mb-0 fs-6">{selectedVisit.notes || 'N/A'}</p>
                  </div>
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {/* Footer content removed */}
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}

export default HouseholdAidHistory;